import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuthState, useSetAuthState } from '../contexts/AuthContext'
import { put } from '../api/request'

export const useMenuPreferences = () => {
  const auth = useAuthState()
  const setAuth = useSetAuthState()
  const [isCollapsed, setIsCollapsed] = useState(false) // Default to expanded
  const [isLoading, setIsLoading] = useState(false)
  const hasPersistedDefault = useRef(false)

  // Initialize menu state from user preferences
  useEffect(() => {
    if (auth.user) {
      // Default to expanded (false) - menu should be expanded by default
      // Only collapse if explicitly set to true
      const menuCollapsed = auth.user.preferences?.menuCollapsed === true
      setIsCollapsed(menuCollapsed)
    }
  }, [auth.user])

  // Save menu state to user preferences
  const updateMenuState = useCallback(
    async (collapsed: boolean) => {
      if (!auth.isAuthenticated || !auth.user) {
        return
      }

      setIsLoading(true)
      try {
        // Update local state immediately for responsive UI
        setIsCollapsed(collapsed)

        // Save to backend
        const response = await put('/ui/users/preferences', {
          preferences: {
            ...auth.user.preferences,
            menuCollapsed: collapsed,
          },
        })

        // Update the auth state with the new user data from the response
        if (response.data) {
          setAuth(prev => ({
            ...prev,
            user: response.data,
          }))
        }
      } catch (error) {
        console.error('Failed to update menu preferences:', error)
        // Revert only if the user previously had a stored preference
        if (auth.user?.preferences?.menuCollapsed !== undefined) {
          setIsCollapsed(!collapsed)
        }
      } finally {
        setIsLoading(false)
      }
    },
    [auth.isAuthenticated, auth.user, setAuth],
  )

  const toggleMenu = useCallback(() => {
    updateMenuState(!isCollapsed)
  }, [isCollapsed, updateMenuState])

  // Persist default expanded state for new users with no stored preference
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user) {
      return
    }

    if (auth.user.preferences?.menuCollapsed === undefined) {
      if (!hasPersistedDefault.current && !isLoading) {
        hasPersistedDefault.current = true
        updateMenuState(false)
      }
    } else {
      hasPersistedDefault.current = true
    }
  }, [auth.isAuthenticated, auth.user, updateMenuState, isLoading])

  return {
    isCollapsed,
    toggleMenu,
    updateMenuState,
    isLoading,
  }
}
